<template>
  <div class="customer-building-list">
    <b-button
      class="print-summary-button"
      @click="printBuildingSummary"
      size="sm"
      variant="secondary"
      >Print Summary</b-button
    >
    <div v-if="loading">
      <CircleLoader></CircleLoader>
    </div>
    <div v-if="!loading">
      <div v-if="buildings.length">
        <b-table
          striped
          :sticky-header="stickyHeader"
          hover
          @row-clicked="goToBuilding"
          :items="buildings"
          :fields="fields"
        >
          <template #cell(dimensions)="data">
            {{ formatDimensions(data.item) }}
          </template>
          
          <template #cell(quoteStatus)="data">
            <div class="quote-status-container">
              <b-badge 
                :variant="getQuoteStatusVariant(data.item.quoteStatus)"
                class="quote-status-badge"
                @click.stop="showFollowUpModal(data.item)"
              >
                {{ getQuoteStatusText(data.item.quoteStatus) }}
              </b-badge>
              <i 
                class="fas fa-file-alt quote-document-icon"
                @click.stop="showFollowUpModal(data.item)"
                title="View quote details and follow-ups"
              ></i>
            </div>
          </template>
        </b-table>
      </div>
      <div v-if="!buildings.length">- No buildings -</div>
    </div>
    <div v-show="false" id="building-summary">
      <div v-if="buildings">
        <QuoteSummary :buildings="buildings"></QuoteSummary>
      </div>
    </div>

    <!-- Follow-up Modal -->
    <b-modal
      id="followup-modal"
      :title="`Follow up: ${selectedQuote && (selectedQuote.nickname || selectedQuote.shortId)}`"
      size="lg"
      @hidden="resetModal"
    >
      <div v-if="selectedQuote">
        <!-- Customer Information Summary -->
        <div class="customer-info-section mb-4">
          <div class="customer-header">
            <h6 class="customer-title">
              <i class="fas fa-user-circle me-2 text-primary"></i>
              Customer Information
            </h6>
          </div>
          
          <!-- Customer Details -->
          <div v-if="hasCustomerData(selectedQuote.customer)" class="customer-details">
            <div class="customer-card">
              <div class="customer-main-info">
                <div class="customer-name">
                  <strong>{{ selectedQuote.customer.name }}</strong>
                </div>
                <div v-if="selectedQuote.customer.company" class="customer-company text-muted">
                  {{ selectedQuote.customer.company }}
                </div>
              </div>
              <div class="customer-contact-info">
                <div v-if="selectedQuote.customer.email" class="contact-item">
                  <i class="fas fa-envelope text-muted me-2"></i>
                  <span>{{ selectedQuote.customer.email }}</span>
                </div>
                <div v-if="selectedQuote.customer.phone" class="contact-item">
                  <i class="fas fa-phone text-muted me-2"></i>
                  <span>{{ selectedQuote.customer.phone }}</span>
                </div>
              </div>
            </div>
          </div>
          
          <!-- No Customer State -->
          <div v-else class="no-customer-message">
            <div class="text-center text-muted py-3">
              <i class="fas fa-user-slash fa-2x mb-2"></i>
              <p class="mb-0">No Customer</p>
              <small>This quote was created without customer information</small>
            </div>
          </div>
          
          <!-- Quote Summary -->
          <div class="quote-summary mt-3">
            <div class="row">
              <div class="col-md-6">
                <div class="summary-item">
                  <strong>Quote Value:</strong> {{ getQuoteValue(selectedQuote) }}
                </div>
                <div class="summary-item">
                  <strong>Building Type:</strong> {{ selectedQuote.type || 'Not specified' }}
                </div>
              </div>
              <div class="col-md-6">
                <div class="summary-item">
                  <strong>Dimensions:</strong> {{ getQuoteDimensions(selectedQuote) }}
                </div>
                <div class="summary-item">
                  <strong>Created:</strong> {{ formatDate(selectedQuote.createdAt) }}
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Follow-up Tabs -->
        <b-tabs v-model="activeModalTab">
          <!-- New Follow-up Tab -->
          <b-tab title="📝 New Follow-up" active>
            <div class="tab-content-padding">
              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Follow-up Status</label>
                    <b-form-select v-model="followUpForm.status" :options="quoteStatusOptions" />
                  </div>
                </div>
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Priority</label>
                    <b-form-select v-model="followUpForm.priority" :options="priorityOptions" />
                  </div>
                </div>
              </div>

              <div class="row">
                <div class="col-md-6">
                  <div class="mb-3">
                    <label class="form-label">Next Follow-up Date</label>
                    <div class="follow-up-date-controls mb-2">
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="setFollowUpDate(7)"
                      >
                        1 Week
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary me-2"
                        @click="setFollowUpDate(14)"
                      >
                        2 Weeks
                      </button>
                      <button 
                        type="button" 
                        class="btn btn-sm btn-outline-primary"
                        @click="setFollowUpDate(30)"
                      >
                        1 Month
                      </button>
                    </div>
                    <b-form-input
                      v-model="followUpForm.nextFollowUpDate"
                      type="datetime-local"
                    />
                  </div>
                </div>
              </div>

              <div class="mb-3">
                <label class="form-label">Follow-up Notes</label>
                <b-form-textarea
                  v-model="followUpForm.note"
                  rows="4"
                  placeholder="Add notes about this follow-up..."
                />
              </div>
            </div>
          </b-tab>

          <!-- Past Follow-ups Tab -->
          <b-tab title="📋 Past Follow-ups">
            <div class="tab-content-padding">
              <FollowUpTimeline :follow-ups="selectedQuote.followUps" />
            </div>
          </b-tab>
        </b-tabs>
      </div>

      <template #modal-footer="{ cancel }">
        <b-button variant="secondary" @click="cancel()">
          Cancel
        </b-button>
        <b-button 
          v-if="activeModalTab === 0"
          variant="success" 
          @click="submitFollowUp"
          :disabled="submitting"
        >
          <span v-if="submitting">Saving...</span>
          <span v-else>Save Follow-up</span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import _ from 'lodash'
import CircleLoader from '@/components/CircleLoader.vue'
import axios from 'axios'
import QuoteSummary from '../components/QuoteSummary.vue'
import FollowUpTimeline from '@/components/FollowUpTimeline.vue'
import moment from 'moment'

export default {
  name: 'CustomerBuildingList',
  components: { CircleLoader, QuoteSummary, FollowUpTimeline },
  props: {
    customer: {
      type: Object,
      required: true,
    },
  },
  data: function () {
    return {
      buildings: null,
      loading: true,
      stickyHeader: true,
      selectedQuote: null,
      submitting: false,
      activeModalTab: 0,
      followUpForm: {
        status: 'IN_PROGRESS',
        priority: 'MEDIUM',
        nextFollowUpDate: '',
        note: ''
      },
      quoteStatusOptions: [
        { value: 'IN_PROGRESS', text: 'In Progress' },
        { value: 'NO_FOLLOW_UP', text: 'No Follow-up' },
        { value: 'CONVERTED', text: 'Converted' },
        { value: 'LOST', text: 'Lost' }
      ],
      priorityOptions: [
        { value: 'LOW', text: 'Low' },
        { value: 'MEDIUM', text: 'Medium' },
        { value: 'HIGH', text: 'High' },
        { value: 'URGENT', text: 'Urgent' }
      ],
      fields: [
        {
          key: 'nickname',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'type',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'dimensions',
          class: ['clickable-row'],
          sortable: true,
        },
        {
          key: 'quoteStatus',
          label: 'Quote Status',
          sortable: true,
        },
        {
          key: 'createdAt',
          label: 'Created',
          class: ['clickable-row'],
          sortable: true,
        },
      ],
    }
  },
  computed: {},
  mounted() {
    this.getBuildings()
  },
  methods: {
    printBuildingSummary() {
      var elem = 'building-summary'
      // todo: add styles for printing..

      // Get HTML to print from element
      const prtHtml = document.getElementById(elem).innerHTML

      // Get all stylesheets HTML
      let stylesHtml = ''
      for (const node of [
        ...document.querySelectorAll('link[rel="stylesheet"], style'),
      ]) {
        stylesHtml += node.outerHTML
      }

      // Open the print window
      const WinPrint = window.open(
        '',
        '',
        'left=0,top=0,width=800,height=900,toolbar=0,scrollbars=0,status=0',
      )

      WinPrint.document.write(`<!DOCTYPE html>
            <html>
            <head>
                ${stylesHtml}
            </head>
            <body>
                ${prtHtml}
            </body>
            </html>`)

      window.setTimeout(function () {
        WinPrint.focus()
        WinPrint.print()
      }, 1000)

      window.setTimeout(function () {
        WinPrint.document.close()
        WinPrint.close()
      }, 2500)
    },
    goToBuilding(building, index, e) {
      if (e.metaKey) {
        window.open('/app/pricer/' + building.shortId, '_blank')
      } else {
        this.$router.push('/app/pricer/' + building.shortId)
      }
    },
    async getBuildings() {
      try {
        let id = this.customer._id
        let response = await axios.get(
          '/services/customers/buildings?customerId=' + id,
        )

        this.buildings = response.data.buildings.map(building => {
          const {
            shortId,
            nickname,
            type,
            createdAt,
            dimensions,
            price,
            customer,
            foundation,
            quoteStatus,
            followUpPriority,
            nextFollowUpDate,
            lastFollowUpDate,
            followUps,
          } = building
          return {
            nickname: nickname ? `${shortId} - ${nickname}` : shortId,
            type,
            dimensions, // Keep original dimensions object for template formatting
            createdAt: this.$options.filters.shortFullDateFilter(createdAt),
            shortId,
            price,
            customer,
            quoteStatus: quoteStatus || 'IN_PROGRESS',
            followUpPriority,
            nextFollowUpDate,
            lastFollowUpDate,
            followUps: followUps || [],
            kitPrice: this.$options.filters.currencyFilter(price.kit.total),
            kitAndInstallPrice: this.$options.filters.currencyFilter(
              price.kitAndInstall.total,
            ),
            concretePrice: this.$options.filters.currencyFilter(
              foundation.price.total,
            ),
            // Keep all original building data for the modal
            ...building
          }
        })
      } catch (error) {
        console.log('error: ', error)
      }
      this.loading = false
    },

    async showFollowUpModal(building) {
      // Fetch fresh building data to ensure we have the latest followUps
      try {
        const response = await axios.get(`/services/v2/quotes/${building.shortId}`)
        this.selectedQuote = response.data.quote || building
      } catch (error) {
        console.error('Error fetching building details:', error)
        // Fallback to the passed building if API fails
        this.selectedQuote = building
      }
      
      this.followUpForm = {
        status: this.selectedQuote.quoteStatus || 'IN_PROGRESS',
        priority: this.selectedQuote.followUpPriority || 'MEDIUM',
        nextFollowUpDate: this.selectedQuote.nextFollowUpDate
          ? moment(this.selectedQuote.nextFollowUpDate).format('YYYY-MM-DDTHH:mm')
          : moment().add(7, 'days').format('YYYY-MM-DDTHH:mm'), // Default to 1 week
        note: ''
      }
      this.activeModalTab = 0
      this.$bvModal.show('followup-modal')
    },

    resetModal() {
      this.selectedQuote = null
      this.activeModalTab = 0
    },

    setFollowUpDate(days) {
      const date = moment().add(days, 'days').format('YYYY-MM-DDTHH:mm')
      this.followUpForm.nextFollowUpDate = date
    },

    async submitFollowUp() {
      if (!this.selectedQuote || !this.followUpForm.note.trim()) {
        this.$bvToast.toast('Please add a note for this follow-up', {
          title: 'Missing Information',
          variant: 'warning'
        })
        return
      }

      try {
        this.submitting = true
        
        const followUpData = {
          quoteStatus: this.followUpForm.status,
          followUpPriority: this.followUpForm.priority,
          nextFollowUpDate: this.followUpForm.nextFollowUpDate || null,
          note: this.followUpForm.note
        }

        await axios.put(`/services/v2/quotes/${this.selectedQuote.shortId}/follow-up`, followUpData)
        
        this.$bvToast.toast('Follow-up saved successfully', {
          title: 'Success',
          variant: 'success'
        })
        
        this.$bvModal.hide('followup-modal')
        
        // Refresh the buildings list to show updated status
        await this.getBuildings()
        
      } catch (error) {
        console.error('Error saving follow-up:', error)
        this.$bvToast.toast('Error saving follow-up', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.submitting = false
      }
    },

    getQuoteStatusVariant(status) {
      const variants = {
        'IN_PROGRESS': 'primary',
        'NO_FOLLOW_UP': 'secondary',
        'CONVERTED': 'success',
        'LOST': 'danger'
      }
      return variants[status] || 'secondary'
    },

    getQuoteStatusText(status) {
      const texts = {
        'IN_PROGRESS': 'In Progress',
        'NO_FOLLOW_UP': 'No Follow-up',
        'CONVERTED': 'Converted',
        'LOST': 'Lost'
      }
      return texts[status] || status || 'In Progress'
    },

    hasCustomerData(customer) {
      return customer && (customer.name || customer.email || customer.phone)
    },

    getQuoteValue(quote) {
      if (!quote || !quote.price) return '$0.00'
      
      const kitTotal = quote.price.kit && quote.price.kit.total
      const kitAndInstallTotal = quote.price.kitAndInstall && quote.price.kitAndInstall.total
      
      const value = kitAndInstallTotal || kitTotal || 0
      
      if (isNaN(value) || typeof value !== 'number') {
        return '$0.00'
      }
      
      return '$' + parseFloat(value).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',')
    },

    getQuoteDimensions(quote) {
      if (!quote || !quote.dimensions) return 'Not specified'
      const { width, length, height } = quote.dimensions
      return `${width || 0}×${length || 0}×${height || 0}`
    },

    formatDate(date) {
      if (!date) return '-'
      return moment(date).format('MMM DD, YYYY')
    },

    formatDimensions(building) {
      if (!building || !building.dimensions) return 'Not specified'
      
      const dims = building.dimensions
      
      // Handle if dimensions is already a string (shouldn't happen but just in case)
      if (typeof dims === 'string') {
        return dims
      }
      
      // Handle if dimensions is an object
      if (typeof dims === 'object' && dims.width && dims.length && dims.height) {
        return `${dims.width}×${dims.length}×${dims.height}`
      }
      
      return 'Not specified'
    },
  },
}
</script>

<style lang="scss" scoped>
.print-summary-button {
  position: absolute;
  top: 0px;
  right: 0px;
}

.quote-status-container {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.quote-status-badge {
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    transform: scale(1.05);
    box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  }
}

.quote-document-icon {
  color: #6c757d;
  cursor: pointer;
  font-size: 1.1rem;
  transition: all 0.2s ease;
  
  &:hover {
    color: #495057;
    transform: scale(1.1);
  }
}

.customer-info-section {
  background: #f8f9fa;
  border-radius: 0.375rem;
  padding: 1rem;
  border: 1px solid #e9ecef;
}

.customer-title {
  color: #495057;
  margin-bottom: 1rem;
  font-weight: 600;
}

.customer-card {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  background: white;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.customer-main-info {
  flex: 1;
}

.customer-name {
  font-size: 1.1rem;
  margin-bottom: 0.5rem;
}

.customer-company {
  font-size: 0.9rem;
}

.customer-contact-info {
  text-align: right;
}

.contact-item {
  margin-bottom: 0.25rem;
  font-size: 0.9rem;
}

.no-customer-message {
  background: white;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.quote-summary {
  background: white;
  padding: 1rem;
  border-radius: 0.375rem;
  border: 1px solid #e9ecef;
}

.summary-item {
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
}

.tab-content-padding {
  padding: 1rem 0;
}

.follow-up-date-controls {
  display: flex;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
}
</style>
